<template>
	<div
		class="analytics-kpi-card relative flex min-h-[142px] flex-col rounded-xl bg-surface-2 p-4 ring-1 ring-inset ring-white/[0.05] shadow-[0_2px_6px_rgba(0,0,0,0.28)]"
		:data-testid="`analytics-kpi-${metricKey}`"
	>
		<!-- Single-line label so every card's value sits on the same baseline; the full label lives in the title. -->
		<div class="flex h-4 items-center gap-1.5">
			<h3 class="mb-0 min-w-0 truncate text-xs font-medium leading-4 text-surface-7" :title="label">{{ label }}</h3>
			<Tooltip v-if="definition" :content="definition" popper-class="max-w-[260px] text-sm break-words">
				<span class="flex size-4 shrink-0 items-center justify-center text-surface-6 hover:text-surface-8 cursor-help" tabindex="0" :aria-label="`What is ${label}?`">
					<icon-base :width="14" :height="14" width-view-box="16" height-view-box="16">
						<icon-info-small />
					</icon-base>
				</span>
			</Tooltip>
		</div>

		<!-- Every state below is exactly the same height (value row + two caption
			rows) so the five cards share one baseline whether Compare is on or off. -->

		<!-- Loading -->
		<div v-if="loading" class="mt-3 flex flex-col gap-1.5" aria-busy="true">
			<div class="h-8 w-24 animate-pulse rounded bg-surface-3" />
			<div class="flex h-5 items-center"><div class="h-4 w-16 animate-pulse rounded bg-surface-3/70" /></div>
			<div class="flex h-4 items-center"><div class="h-3 w-28 animate-pulse rounded bg-surface-3/50" /></div>
		</div>

		<div v-else-if="error" class="mt-3 flex flex-col gap-1.5">
			<div class="h-8 text-[28px] font-semibold leading-8 tabular-nums text-surface-6">--</div>
			<button type="button" class="flex h-5 items-center self-start text-xs leading-5 text-surface-8 underline-offset-2 hover:text-white hover:underline" @click="$emit('retry')">
				Data unavailable. Retry
			</button>
			<div class="h-4" aria-hidden="true" />
		</div>

		<div v-else class="mt-3 flex flex-col gap-1.5">
			<!-- Fixed-height value row: the value never wraps; long values drop a step in size instead. -->
			<div class="flex h-8 items-end">
				<div class="min-w-0 whitespace-nowrap font-semibold tracking-tight tabular-nums text-surface-text-2" :class="valueSizeClass" :title="valueTitle">
					{{ value }}
				</div>
			</div>
			<div class="flex items-end justify-between gap-2">
				<div class="flex min-w-0 flex-1 flex-col gap-1.5">
					<!-- Row 1: delta chip (Compare on) or the period caption (Compare off). -->
					<div class="flex h-5 items-center">
						<template v-if="compare">
							<AnalyticsDeltaChip v-if="!compareLoading" :delta="delta" />
							<div v-else class="h-4 w-14 animate-pulse rounded bg-surface-3/70" aria-hidden="true" />
						</template>
						<span v-else class="truncate text-[11px] leading-4 text-surface-6" :title="rangeCaption">{{ rangeCaption }}</span>
					</div>
					<!-- Row 2: "vs previous 7 days"; kept (empty) when Compare is off so heights match. -->
					<div class="flex h-4 items-center">
						<span v-if="compare" class="truncate whitespace-nowrap text-[11px] leading-4 text-surface-6" :title="compareSuffix">{{ compareSuffix }}</span>
					</div>
				</div>
				<!-- Sparkline only when the series is already loaded; the slot keeps its size so cards do not jump. Hidden where five cards share a row below xl. -->
				<AnalyticsSparkline v-if="sparkline && sparkline.length >= 2" :values="sparkline" class="hidden sm:block lg:hidden xl:block" />
				<div v-else class="hidden h-7 w-16 shrink-0 sm:block lg:hidden xl:block" aria-hidden="true" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconInfoSmall from '@/components/icon/IconInfoSmall.vue';
import AnalyticsSparkline from './AnalyticsSparkline.vue';
import AnalyticsDeltaChip from './AnalyticsDeltaChip.vue';

const props = defineProps({
	metricKey: { type: String, required: true },
	label: { type: String, required: true },
	// Already formatted for display.
	value: { type: String, default: '0' },
	valueTitle: { type: String, default: '' },
	definition: { type: String, default: '' },
	delta: { type: Object, default: null },
	compare: { type: Boolean, default: false },
	compareLoading: { type: Boolean, default: false },
	compareSuffix: { type: String, default: '' },
	rangeCaption: { type: String, default: '' },
	sparkline: { type: Array, default: null },
	loading: { type: Boolean, default: false },
	error: { type: Boolean, default: false },
});

defineEmits(['retry']);

// "2,425" and "1.3K h" fit at 28px next to the sparkline; "891h 18m" or
// "12m 08s" step down so the row height stays constant across cards.
const valueSizeClass = computed(() => (props.value.length > 6 ? 'text-2xl leading-8' : 'text-[28px] leading-8'));
</script>
